import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Loader2, Pencil, Trash2 } from "lucide-react";

import { AddressMapPreview } from "@/components/shared/AddressMapPreview";
import { ConfirmationDialog } from "@/components/shared/ConfirmationDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import {
  deleteUserAddress,
  updateUserAddress,
  type UserAddressDto,
  type UserAddressCreatePayload,
} from "@/services/userAddressesService";
import { loadGoogleMapsScript } from "@/utils/loadGoogleMapsScript";
import { googlePlaceClassToPayload } from "@/utils/googlePlaceToAddressPayload";

function isEditFormAddressComplete(f: Partial<UserAddressCreatePayload>): boolean {
  const hasPlace = Boolean(f.googlePlaceId?.trim());
  const hasFormatted = Boolean(f.formattedAddress?.trim());
  const hasStructured = Boolean(
    f.route?.trim() ||
      f.city?.trim() ||
      f.neighborhood?.trim() ||
      f.adminArea?.trim(),
  );
  return hasPlace || hasFormatted || hasStructured;
}

type PlaceAutocompleteLike = HTMLElement & {
  includedRegionCodes?: string[];
  placeholder?: string;
};

function isPlaceAutocompleteCtor(x: unknown): x is new (opts?: object) => PlaceAutocompleteLike {
  return typeof x === "function";
}

export function AddressPickerSavedList({
  addresses,
  selectedId,
  onSelect,
  disabled,
  idPrefix,
}: {
  addresses: UserAddressDto[];
  selectedId: string | undefined;
  onSelect: (row: UserAddressDto) => void;
  disabled?: boolean;
  idPrefix: string;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<UserAddressDto | null>(null);
  const [editForm, setEditForm] = useState<Partial<UserAddressCreatePayload>>({});
  const [deleting, setDeleting] = useState<UserAddressDto | null>(null);
  const [editAutocompleteReady, setEditAutocompleteReady] = useState(false);
  const editAutocompleteHostRef = useRef<HTMLDivElement>(null);
  const editPacRef = useRef<PlaceAutocompleteLike | null>(null);
  const editFormRef = useRef(editForm);
  editFormRef.current = editForm;

  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY?.trim() ?? "";

  useEffect(() => {
    if (!editing || !apiKey) {
      setEditAutocompleteReady(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        await loadGoogleMapsScript(apiKey);
        await google.maps.importLibrary("places");
        if (!cancelled) setEditAutocompleteReady(true);
      } catch {
        if (!cancelled) setEditAutocompleteReady(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editing, apiKey]);

  useEffect(() => {
    if (!editing || !editAutocompleteReady || !apiKey) {
      return;
    }
    const host = editAutocompleteHostRef.current;
    const PlaceCtor = google.maps.places.PlaceAutocompleteElement;
    if (!host || !isPlaceAutocompleteCtor(PlaceCtor)) {
      return;
    }

    host.replaceChildren();
    const pac = new PlaceCtor({}) as PlaceAutocompleteLike;
    pac.includedRegionCodes = ["uy"];
    pac.placeholder = t("addressPicker.searchPlaceholder");
    pac.className =
      "flex h-10 w-full rounded-md border border-input text-sm ring-offset-background transition-colors duration-150 motion-reduce:transition-none";

    const onSelect = async (ev: Event): Promise<void> => {
      const withPred = ev as Event & {
        placePrediction?: { toPlace: () => google.maps.places.Place };
      };
      const fromDetail = (
        ev as CustomEvent<{ placePrediction?: { toPlace: () => google.maps.places.Place } }>
      ).detail?.placePrediction;
      const prediction = withPred.placePrediction ?? fromDetail;
      if (!prediction) return;
      const place = prediction.toPlace();
      await place.fetchFields({
        fields: ["id", "formattedAddress", "location", "addressComponents"],
      });
      if (!place.location) return;
      const prev = editFormRef.current;
      const placePayload = googlePlaceClassToPayload(place);
      setEditForm({
        ...prev,
        ...placePayload,
        countryCode: placePayload.countryCode ?? prev.countryCode ?? "UY",
        label: prev.label,
        contactPhone: prev.contactPhone,
        contactPersonName: prev.contactPersonName,
        courierNotes: prev.courierNotes,
      });
    };

    pac.addEventListener("gmp-select", (e) => void onSelect(e));
    host.appendChild(pac);
    editPacRef.current = pac;

    return () => {
      editPacRef.current = null;
      host.replaceChildren();
    };
  }, [editing?.id, editAutocompleteReady, apiKey, t, i18n.language]);

  useEffect(() => {
    if (!editing) {
      setEditForm({});
      return;
    }
    setEditForm({
      label: editing.label ?? undefined,
      formattedAddress: editing.formattedAddress ?? undefined,
      googlePlaceId: editing.googlePlaceId ?? undefined,
      latitude: editing.latitude ?? undefined,
      longitude: editing.longitude ?? undefined,
      route: editing.route ?? undefined,
      streetNumber: editing.streetNumber ?? undefined,
      addressLine2: editing.addressLine2 ?? undefined,
      neighborhood: editing.neighborhood ?? undefined,
      city: editing.city ?? undefined,
      adminArea: editing.adminArea ?? undefined,
      postalCode: editing.postalCode ?? undefined,
      countryCode: editing.countryCode ?? "UY",
      contactPhone: editing.contactPhone ?? undefined,
      contactPersonName: editing.contactPersonName ?? undefined,
      courierNotes: editing.courierNotes ?? undefined,
    });
  }, [editing]);

  const closeEdit = useCallback(() => setEditing(null), []);

  const updateMutation = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: Partial<UserAddressCreatePayload> }) =>
      updateUserAddress(id, body),
    onSuccess: () => {
      toast.success(t("addressPicker.editSuccess"));
      void queryClient.invalidateQueries({ queryKey: ["me", "addresses"] });
      closeEdit();
    },
    onError: () => {
      toast.error(t("addressPicker.editError"));
    },
  });

  useEffect(() => {
    const el = editPacRef.current;
    if (!el) return;
    if (updateMutation.isPending) {
      el.setAttribute("disabled", "");
    } else {
      el.removeAttribute("disabled");
    }
  }, [updateMutation.isPending, editAutocompleteReady, editing?.id]);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteUserAddress(id),
    onSuccess: () => {
      toast.success(t("addressPicker.deleteSuccess"));
      void queryClient.invalidateQueries({ queryKey: ["me", "addresses"] });
      setDeleting(null);
    },
    onError: () => {
      toast.error(t("addressPicker.deleteError"));
    },
  });

  const handleSaveEdit = useCallback(() => {
    if (!editing) return;
    updateMutation.mutate({ id: editing.id, body: editForm });
  }, [editing, editForm, updateMutation]);

  const showEditDetailFields =
    !apiKey ||
    Boolean(editForm.googlePlaceId?.trim()) ||
    isEditFormAddressComplete(editForm);
  const mapLat = editForm.latitude;
  const mapLng = editForm.longitude;
  const showEditMap =
    Boolean(apiKey) &&
    showEditDetailFields &&
    typeof mapLat === "number" &&
    typeof mapLng === "number" &&
    Number.isFinite(mapLat) &&
    Number.isFinite(mapLng);

  if (addresses.length === 0) {
    return <p className="text-xs text-muted-foreground">{t("addressPicker.noSavedAddresses")}</p>;
  }

  return (
    <>
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground">{t("addressPicker.savedCardsHint")}</p>
        <ul className="space-y-2" role="list">
          {addresses.map((a) => {
            const isSelected = selectedId === a.id;
            const mainAddress = (a.formattedAddress?.trim() || t("addressPicker.noFormatted")).trim();
            const labelTrim = a.label?.trim() ?? "";
            const checkboxId = `${idPrefix}-pick-${a.id}`;
            return (
              <li key={a.id}>
                <Card
                  className={cn(
                    "border transition-colors duration-150 motion-reduce:transition-none",
                    isSelected
                      ? "border-primary bg-primary/5 ring-1 ring-primary/25"
                      : "border-border hover:border-muted-foreground/30",
                  )}
                >
                  <CardContent className="flex flex-col gap-3 p-3 sm:flex-row sm:items-start">
                    <div className="flex min-w-0 flex-1 gap-3">
                      <div className="flex shrink-0 items-start pt-0.5">
                        <Checkbox
                          id={checkboxId}
                          checked={isSelected}
                          disabled={disabled}
                          onCheckedChange={(v) => {
                            if (v === true) onSelect(a);
                          }}
                          className="transition-colors duration-150"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <label htmlFor={checkboxId} className="block cursor-pointer">
                          {labelTrim ? (
                            <>
                              <span className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                {labelTrim}
                              </span>
                              <span className="mt-1 block text-sm font-medium leading-snug text-foreground">
                                {mainAddress}
                              </span>
                            </>
                          ) : (
                            <span className="block text-sm font-medium leading-snug text-foreground">
                              {mainAddress}
                            </span>
                          )}
                        </label>
                        {a.isDefault ? (
                          <Badge variant="secondary" className="mt-1.5 text-xs font-normal">
                            {t("addressPicker.defaultBadge")}
                          </Badge>
                        ) : null}
                        {a.contactPhone?.trim() ? (
                          <p className="mt-1.5 text-xs text-muted-foreground">
                            {t("addressPicker.savedCardPhone", { phone: a.contactPhone.trim() })}
                          </p>
                        ) : null}
                        {a.contactPersonName?.trim() ? (
                          <p className="text-xs text-muted-foreground">
                            {t("addressPicker.savedCardPerson", {
                              name: a.contactPersonName.trim(),
                            })}
                          </p>
                        ) : null}
                        {a.courierNotes?.trim() ? (
                          <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                            {t("addressPicker.savedCardCourierNotes", {
                              notes: a.courierNotes.trim(),
                            })}
                          </p>
                        ) : null}
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-row justify-end gap-1 sm:flex-col sm:justify-start">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={disabled}
                        className="h-8 px-2 text-muted-foreground transition-colors duration-150 hover:text-foreground"
                        aria-label={t("addressPicker.editAddress")}
                        onClick={(e) => {
                          e.preventDefault();
                          setEditing(a);
                        }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={disabled}
                        className="h-8 px-2 text-muted-foreground transition-colors duration-150 hover:text-destructive"
                        aria-label={t("addressPicker.deleteAddress")}
                        onClick={(e) => {
                          e.preventDefault();
                          setDeleting(a);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </li>
            );
          })}
        </ul>
      </div>

      <Dialog open={editing != null} onOpenChange={(open) => !open && closeEdit()}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl motion-reduce:transition-none">
          <DialogHeader className="space-y-1.5 pb-1 text-left">
            <DialogTitle className="text-foreground">{t("addressPicker.editDialogTitle")}</DialogTitle>
            <DialogDescription className="text-pretty">
              {t("addressPicker.editDialogDescription")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 sm:space-y-6">
            <section
              className="rounded-lg border border-border bg-muted/20 p-4 sm:p-5 motion-reduce:transition-none"
              aria-labelledby={`${idPrefix}-edit-sec-label`}
            >
              <h3
                id={`${idPrefix}-edit-sec-label`}
                className="mb-3 text-sm font-semibold leading-none text-foreground"
              >
                {t("addressPicker.editSectionLabel")}
              </h3>
              <div className="space-y-2">
                <Input
                  id={`${idPrefix}-edit-label`}
                  value={editForm.label ?? ""}
                  onChange={(e) => setEditForm((f) => ({ ...f, label: e.target.value || undefined }))}
                  placeholder={t("addressPicker.labelPlaceholder")}
                  disabled={updateMutation.isPending}
                  className="transition-colors duration-150"
                />
              </div>
            </section>

            <div className="space-y-5 sm:space-y-6">
              {apiKey ? (
                <section
                  className="rounded-lg border border-border bg-muted/20 p-4 sm:p-5 motion-reduce:transition-none"
                  aria-labelledby={`${idPrefix}-edit-sec-search`}
                >
                  <h3
                    id={`${idPrefix}-edit-sec-search`}
                    className="mb-3 text-sm font-semibold leading-none text-foreground"
                  >
                    {t("addressPicker.editSectionSearch")}
                  </h3>
                  <div className="space-y-2">
                    <div id={`${idPrefix}-edit-search`} ref={editAutocompleteHostRef} className="w-full" />
                    {!editAutocompleteReady ? (
                      <p className="text-xs text-muted-foreground">{t("addressPicker.mapsLoading")}</p>
                    ) : null}
                    {editAutocompleteReady && !showEditDetailFields ? (
                      <p className="text-xs text-muted-foreground">{t("addressPicker.selectPlaceHint")}</p>
                    ) : null}
                  </div>
                </section>
              ) : (
                <p className="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-800 dark:border-amber-400/25 dark:bg-amber-400/10 dark:text-amber-200">
                  {t("addressPicker.noApiKeyHint")}
                </p>
              )}

              {showEditDetailFields ? (
                <>
                  <Separator className="bg-border" />

                  <section
                    className="rounded-lg border border-border bg-muted/20 p-4 sm:p-5 motion-reduce:transition-none"
                    aria-labelledby={`${idPrefix}-edit-sec-address`}
                  >
                    <h3
                      id={`${idPrefix}-edit-sec-address`}
                      className="mb-4 text-sm font-semibold leading-none text-foreground"
                    >
                      {t("addressPicker.editSectionAddress")}
                    </h3>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2 sm:col-span-2">
                        <Label htmlFor={`${idPrefix}-edit-formatted`} className="text-foreground">
                          {t("addressPicker.formattedLabel")}
                        </Label>
                        <Input
                          id={`${idPrefix}-edit-formatted`}
                          value={editForm.formattedAddress ?? ""}
                          onChange={(e) =>
                            setEditForm((f) => ({ ...f, formattedAddress: e.target.value || undefined }))
                          }
                          placeholder={t("addressPicker.formattedPlaceholder")}
                          disabled={updateMutation.isPending}
                          className="transition-colors duration-150"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor={`${idPrefix}-edit-route`} className="text-foreground">
                          {t("addressPicker.routeLabel")}
                        </Label>
                        <Input
                          id={`${idPrefix}-edit-route`}
                          value={editForm.route ?? ""}
                          onChange={(e) =>
                            setEditForm((f) => ({ ...f, route: e.target.value || undefined }))
                          }
                          disabled={updateMutation.isPending}
                          className="transition-colors duration-150"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor={`${idPrefix}-edit-num`} className="text-foreground">
                          {t("addressPicker.streetNumberLabel")}
                        </Label>
                        <Input
                          id={`${idPrefix}-edit-num`}
                          value={editForm.streetNumber ?? ""}
                          onChange={(e) =>
                            setEditForm((f) => ({ ...f, streetNumber: e.target.value || undefined }))
                          }
                          disabled={updateMutation.isPending}
                          className="transition-colors duration-150"
                        />
                      </div>
                      <div className="space-y-2 sm:col-span-2">
                        <Label htmlFor={`${idPrefix}-edit-line2`} className="text-foreground">
                          {t("addressPicker.addressLine2Label")}
                        </Label>
                        <Input
                          id={`${idPrefix}-edit-line2`}
                          value={editForm.addressLine2 ?? ""}
                          onChange={(e) =>
                            setEditForm((f) => ({ ...f, addressLine2: e.target.value || undefined }))
                          }
                          placeholder={t("addressPicker.addressLine2Placeholder")}
                          disabled={updateMutation.isPending}
                          className="transition-colors duration-150"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor={`${idPrefix}-edit-city`} className="text-foreground">
                          {t("addressPicker.cityLabel")}
                        </Label>
                        <Input
                          id={`${idPrefix}-edit-city`}
                          value={editForm.city ?? ""}
                          onChange={(e) =>
                            setEditForm((f) => ({ ...f, city: e.target.value || undefined }))
                          }
                          disabled={updateMutation.isPending}
                          className="transition-colors duration-150"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor={`${idPrefix}-edit-dept`} className="text-foreground">
                          {t("addressPicker.adminAreaLabel")}
                        </Label>
                        <Input
                          id={`${idPrefix}-edit-dept`}
                          value={editForm.adminArea ?? ""}
                          onChange={(e) =>
                            setEditForm((f) => ({ ...f, adminArea: e.target.value || undefined }))
                          }
                          disabled={updateMutation.isPending}
                          className="transition-colors duration-150"
                        />
                      </div>
                    </div>
                    {showEditMap ? (
                      <AddressMapPreview
                        className="pt-4"
                        lat={mapLat}
                        lng={mapLng}
                        caption={t("addressPicker.mapPreviewCaption")}
                        disableInteractions={updateMutation.isPending}
                        mapsApiKey={apiKey}
                      />
                    ) : null}
                  </section>

                  <section
                    className="rounded-lg border border-border bg-muted/20 p-4 sm:p-5 motion-reduce:transition-none"
                    aria-labelledby={`${idPrefix}-edit-sec-contact`}
                  >
                    <h3
                      id={`${idPrefix}-edit-sec-contact`}
                      className="mb-4 text-sm font-semibold leading-none text-foreground"
                    >
                      {t("addressPicker.editSectionContact")}
                    </h3>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2 sm:col-span-2">
                        <Label htmlFor={`${idPrefix}-edit-phone`} className="text-foreground">
                          {t("addressPicker.contactPhoneLabel")}
                        </Label>
                        <Input
                          id={`${idPrefix}-edit-phone`}
                          type="tel"
                          autoComplete="tel"
                          value={editForm.contactPhone ?? ""}
                          onChange={(e) =>
                            setEditForm((f) => ({ ...f, contactPhone: e.target.value || undefined }))
                          }
                          placeholder={t("addressPicker.contactPhonePlaceholder")}
                          disabled={updateMutation.isPending}
                          className="transition-colors duration-150"
                        />
                      </div>
                      <div className="space-y-2 sm:col-span-2">
                        <Label htmlFor={`${idPrefix}-edit-person`} className="text-foreground">
                          {t("addressPicker.contactPersonLabel")}
                        </Label>
                        <Input
                          id={`${idPrefix}-edit-person`}
                          autoComplete="name"
                          value={editForm.contactPersonName ?? ""}
                          onChange={(e) =>
                            setEditForm((f) => ({ ...f, contactPersonName: e.target.value || undefined }))
                          }
                          placeholder={t("addressPicker.contactPersonPlaceholder")}
                          disabled={updateMutation.isPending}
                          className="transition-colors duration-150"
                        />
                      </div>
                    </div>
                  </section>

                  <section
                    className="rounded-lg border border-border bg-muted/20 p-4 sm:p-5 motion-reduce:transition-none"
                    aria-labelledby={`${idPrefix}-edit-sec-courier`}
                  >
                    <h3
                      id={`${idPrefix}-edit-sec-courier`}
                      className="mb-3 text-sm font-semibold leading-none text-foreground"
                    >
                      {t("addressPicker.editSectionCourier")}
                    </h3>
                    <div className="space-y-2">
                      <Label htmlFor={`${idPrefix}-edit-courier`} className="sr-only">
                        {t("requests.user.deliveryNotesLabel")}
                      </Label>
                      <Textarea
                        id={`${idPrefix}-edit-courier`}
                        rows={3}
                        value={editForm.courierNotes ?? ""}
                        onChange={(e) =>
                          setEditForm((f) => ({ ...f, courierNotes: e.target.value || undefined }))
                        }
                        placeholder={t("requests.user.deliveryNotesPlaceholder")}
                        disabled={updateMutation.isPending}
                        aria-label={t("requests.user.deliveryNotesLabel")}
                        className="transition-colors duration-150"
                      />
                    </div>
                  </section>
                </>
              ) : null}
            </div>
          </div>

          <DialogFooter className="mt-6 flex w-full flex-row justify-between gap-2 border-t border-border pt-4 sm:justify-between">
            <Button
              type="button"
              variant="outline"
              className="transition-colors duration-150"
              onClick={closeEdit}
              disabled={updateMutation.isPending}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              className="transition-colors duration-150"
              onClick={() => void handleSaveEdit()}
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                  {t("addressPicker.saving")}
                </>
              ) : (
                t("addressPicker.editDialogSave")
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmationDialog
        open={deleting != null}
        onOpenChange={(open) => !open && setDeleting(null)}
        title={t("addressPicker.deleteConfirmTitle")}
        description={t("addressPicker.deleteConfirmDescription")}
        confirmText={t("addressPicker.deleteConfirmAction")}
        cancelText={t("common.cancel")}
        variant="destructive"
        isLoading={deleteMutation.isPending}
        loadingText={t("addressPicker.deleting")}
        onConfirm={async () => {
          if (deleting) await deleteMutation.mutateAsync(deleting.id);
        }}
      />
    </>
  );
}
